/**
 * CoachStrip Component
 * 
 * A lightweight, non-intrusive guidance strip that shows contextual next-step prompts.
 * Wearable-style: one recommendation at a time, supportive language.
 * 
 * P1 FIX: Improved mobile layout to prevent mid-word truncation and cramped spacing.
 */

import React from 'react';

const CoachStrip = ({ 
  prompt, 
  onPrimaryAction, 
  onSecondaryAction,  // NEW: Handler for secondary CTA
  onDismiss,
  variant = 'default' // 'default' | 'miq' | 'success' | 'commitment' for different backgrounds
}) => {
  if (!prompt) return null;
  
  // Handle isToast prompts separately - render as auto-dismiss toast
  if (prompt.isToast) {
    return (
      <JourneyStartedToast 
        message={prompt.message}
        subMessage={prompt.subMessage}
        icon={prompt.icon}
        onDismiss={onDismiss}
      />
    );
  }
  
  const { message, primaryLabel, icon, secondaryMessage, secondaryCTA, subMessage } = prompt;
  
  // Use subMessage if secondaryMessage not provided (Rules K, L use subMessage)
  const displaySubMessage = secondaryMessage || subMessage;
  
  // Use success variant if specified in prompt
  const effectiveVariant = prompt.variant || variant;
  
  // Determine background based on variant
  const getBackground = () => {
    switch (effectiveVariant) {
      case 'success':
        return 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)';
      case 'miq':
        return 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%)';
      case 'commitment':
        return 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)';
      case 'intelligence':
        // Premium intelligence gradient: dark indigo → soft purple → subtle electric-blue
        return 'linear-gradient(135deg, rgba(30, 27, 75, 0.95) 0%, rgba(88, 28, 135, 0.85) 50%, rgba(79, 70, 229, 0.75) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)';
    }
  };
  
  const getBorder = () => {
    switch (effectiveVariant) {
      case 'success':
        return '1px solid rgba(76, 175, 80, 0.35)';
      case 'miq':
        return '1px solid rgba(102, 126, 234, 0.25)';
      case 'commitment':
        return '1px solid rgba(255, 193, 7, 0.3)';
      case 'intelligence':
        // Electric-blue edge glow
        return '1px solid rgba(99, 102, 241, 0.5)';
      default:
        return '1px solid rgba(76, 175, 80, 0.25)';
    }
  };
  
  const getButtonBackground = () => {
    switch (effectiveVariant) {
      case 'miq':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'commitment':
        return 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)';
      case 'intelligence':
        // Premium purple-indigo gradient for intelligence variant
        return 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)';
      default:
        return 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)';
    }
  };
  
  // Get box shadow for intelligence variant (soft inner glow)
  const getBoxShadow = () => {
    if (effectiveVariant === 'intelligence') {
      return '0 0 20px rgba(99, 102, 241, 0.15), inset 0 0 30px rgba(139, 92, 246, 0.1)';
    }
    return 'none';
  };
  
  return (
    <div 
      className="coach-strip"
      data-testid="coach-strip"
      style={{
        background: getBackground(),
        border: getBorder(),
        borderRadius: '16px',
        padding: '16px 18px',
        marginBottom: '12px',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        animation: 'coachSlideIn 0.3s ease-out',
        boxShadow: getBoxShadow()
      }}
    >
      {/* Icon */}
      <span style={{ 
        fontSize: '22px',
        flexShrink: 0,
        lineHeight: 1
      }}>
        {icon || '💡'}
      </span>
      
      {/* Message Container - Allows natural wrapping */}
      <div style={{ 
        flex: '1 1 200px',
        minWidth: '150px'
      }}>
        <p style={{ 
          margin: 0, 
          fontSize: '14px', 
          color: 'var(--text-primary)',
          lineHeight: '1.5',
          fontWeight: '500',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'auto'
        }}>
          {message}
        </p>
        {displaySubMessage && (
          <p style={{
            margin: '6px 0 0 0',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            lineHeight: '1.4',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {displaySubMessage}
          </p>
        )}
      </div>
      
      {/* Actions - Different layout based on whether secondaryCTA exists */}
      {secondaryCTA ? (
        /* TWO SQUARE BOXES LAYOUT - For dual-CTA prompts */
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            {/* Primary Box */}
            <button
              onClick={onPrimaryAction}
              data-testid="coach-primary-action"
              style={{
                width: '80px',
                height: '80px',
                padding: '8px',
                fontSize: '10px',
                fontWeight: '600',
                background: getButtonBackground(),
                color: effectiveVariant === 'commitment' ? '#1a1a1a' : 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'transform 0.15s ease, opacity 0.15s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              <span style={{ fontSize: '22px' }}>{icon || '👉'}</span>
              <span style={{ lineHeight: '1.2', textAlign: 'center', fontWeight: '700' }}>{primaryLabel?.replace(/^[^\w]*/, '').split(' ').slice(0, 2).join(' ') || 'Go'}</span>
            </button>
            
            {/* Secondary Box */}
            <button
              onClick={() => onSecondaryAction && onSecondaryAction(secondaryCTA.action)}
              data-testid="coach-secondary-action"
              style={{
                width: '80px',
                height: '80px',
                padding: '8px',
                fontSize: '10px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'transform 0.15s ease, opacity 0.15s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              <span style={{ fontSize: '22px' }}>{secondaryCTA.icon || '📊'}</span>
              <span style={{ lineHeight: '1.2', textAlign: 'center', fontWeight: '700' }}>{secondaryCTA.label?.replace(/^[^\w]*/, '').replace(/→$/, '').trim().split(' ').slice(0, 2).join(' ') || 'More'}</span>
            </button>
          </div>
          
          {/* Not now link */}
          <button
            onClick={onDismiss}
            data-testid="coach-dismiss"
            style={{
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              opacity: 0.6
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.6'}
          >
            Not now
          </button>
        </div>
      ) : (
        /* SINGLE CTA LAYOUT - Standard row layout */
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          flexShrink: 0
        }}>
          <button
            onClick={onPrimaryAction}
            data-testid="coach-primary-action"
            style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              background: getButtonBackground(),
              color: effectiveVariant === 'commitment' ? '#1a1a1a' : 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: '44px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'transform 0.15s ease, opacity 0.15s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            {primaryLabel}
          </button>
          
          <button
            onClick={onDismiss}
            data-testid="coach-dismiss"
            style={{
              padding: '10px 14px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              cursor: 'pointer',
              opacity: 0.8,
              whiteSpace: 'nowrap',
              minHeight: '44px'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            Not now
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * JourneyStartedToast Component
 * 
 * RULE M: One-time toast shown when user completes the commitment flow
 * (profile + goals + at least 1 meal logged).
 * Auto-dismisses after 5 seconds. Non-blocking, no CTA needed.
 */
const JourneyStartedToast = ({ 
  message, 
  subMessage,
  icon = '🚀',
  onDismiss
}) => {
  // Auto-dismiss after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (onDismiss) onDismiss();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  
  return (
    <div 
      className="journey-toast"
      data-testid="journey-started-toast"
      style={{
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.95) 0%, rgba(56, 142, 60, 0.95) 100%)',
        borderRadius: '12px',
        padding: '16px 18px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
        animation: 'coachSlideIn 0.3s ease-out'
      }}
    >
      <span style={{ 
        fontSize: '24px',
        lineHeight: 1
      }}>
        {icon}
      </span>
      <div style={{ flex: 1 }}>
        <p style={{ 
          margin: 0, 
          fontSize: '15px', 
          color: 'white',
          lineHeight: '1.4',
          fontWeight: '600'
        }}>
          {message}
        </p>
        {subMessage && (
          <p style={{
            margin: '6px 0 0 0',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            lineHeight: '1.4'
          }}>
            {subMessage}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * CoachToast Component
 * 
 * A brief toast notification shown after key actions (auto-dismisses after ~4 seconds).
 * P1: Updated for better multi-line support and secondary message.
 */
const CoachToast = ({ 
  message, 
  secondaryMessage,
  ctaLabel, 
  onCtaClick, 
  onClose,
  icon = '✨'
}) => {
  return (
    <div 
      className="coach-toast"
      style={{
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(33, 33, 33, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        zIndex: 9999,
        maxWidth: '90vw',
        animation: 'toastSlideUp 0.3s ease-out'
      }}
    >
      <span style={{ fontSize: '20px', lineHeight: 1, marginTop: '2px' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <p style={{ 
          margin: 0, 
          fontSize: '14px', 
          color: 'white',
          lineHeight: '1.5',
          fontWeight: '500'
        }}>
          {message}
        </p>
        {secondaryMessage && (
          <p style={{
            margin: '6px 0 0 0',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.7)',
            lineHeight: '1.4'
          }}>
            {secondaryMessage}
          </p>
        )}
      </div>
      {ctaLabel && (
        <button
          onClick={onCtaClick}
          style={{
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            minHeight: '40px'
          }}
        >
          {ctaLabel}
        </button>
      )}
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.5)',
          cursor: 'pointer',
          fontSize: '18px',
          padding: '8px',
          marginLeft: '4px',
          minWidth: '36px',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>
    </div>
  );
};

export { CoachStrip, CoachToast };
export default CoachStrip;
